import type * as React from "react";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography
} from "@mui/material";
import { withStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COLORS } from "components/v3/Theme/colors";
import {
  GENOMIC_LIFE_PUBLIC_POLICIES_URL,
  GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL
} from "constants/externalPathConstants";

const NAVIGATION_EMAIL = "navigation@genomiclife.com";
const NAVIGATION_MAILTO = `mailto:${NAVIGATION_EMAIL}`;
const NAVIGATION_PHONE_DISPLAY = "844-694-3666";
const NAVIGATION_PHONE_TEL = "tel:+18446943666";

const publicPolicyLinkSx = {
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

const MarketingAccordion = withStyles({
  root: {
    background: "none",
    boxShadow: "none",
    "&.Mui-expanded": {
      "&::before": {
        opacity: 1
      }
    }
  },
  gutters: {
    "&::before": {
      backgroundColor: COLORS.GREEN_2
    }
  }
})(MuiAccordion);

const accordionDetailsSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  pb: 1,
  pt: 0,
  px: 0
} as const;

const bodyParagraphSx = { m: 0 } as const;

const subsectionTitleSx = {
  fontWeight: 700,
  m: 0,
  mt: 0.5
} as const;

const bulletListSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  m: 0,
  pl: 2.5,
  listStyleType: "disc"
} as const;

const FaqBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="div" variant="body_dark_gray" sx={bodyParagraphSx}>
    {children}
  </Typography>
);

const InviteeProductFaqAccordions: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <MarketingAccordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1 }}>
        <Typography component="span" variant="body_strong_lg_black">
          How do I get started?
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <FaqBody>
          To activate your account, you&apos;ll need the personal access code that
          was emailed by the member who referred you. Not sure what your access
          code is or can&apos;t find that email? No worries; simply{" "}
          <Link href={NAVIGATION_MAILTO} sx={publicPolicyLinkSx}>
            contact our team
          </Link>{" "}
          for assistance.
        </FaqBody>
      </AccordionDetails>
    </MarketingAccordion>

    <MarketingAccordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1 }}>
        <Typography component="span" variant="body_strong_lg_black">
          What does Genomic Life do?
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <FaqBody>
          Genomic Life was built to help bridge the gap between what&apos;s
          available in healthcare today and what&apos;s truly personalized. The
          Genomic Life membership offers access to:
        </FaqBody>
        <Typography
          component="p"
          variant="body_strong_dark_gray"
          sx={subsectionTitleSx}
        >
          Prevention &amp; Early Detection
        </Typography>
        <Box component="ul" sx={bulletListSx}>
          <Typography component="li" variant="body_dark_gray">
            Screenings that use the power of your DNA to reveal your personal
            health risks
          </Typography>
          <Typography component="li" variant="body_dark_gray">
            Clear next steps to help reduce those personal risks
          </Typography>
          <Typography component="li" variant="body_dark_gray">
            1-on-1 support throughout, including ways to improve early detection
          </Typography>
        </Box>
        <Typography
          component="p"
          variant="body_strong_dark_gray"
          sx={subsectionTitleSx}
        >
          Personalized Wraparound Guidance
        </Typography>
        <Box component="ul" sx={bulletListSx}>
          <Typography component="li" variant="body_dark_gray">
            If you get a serious illness like cancer, get dedicated 1-on-1 help
          </Typography>
          <Typography component="li" variant="body_dark_gray">
            Your experienced navigator helps you find clarity and direction at a
            time that can feel overwhelming and confusing
          </Typography>
          <Typography component="li" variant="body_dark_gray">
            They can explain your healthcare choices, answer questions, help you
            find the right doctors, work with your care team, and connect you to
            local support
          </Typography>
        </Box>
        <FaqBody>
          We are not a medical company or a health insurance company. We partner
          with the best resources in genomic medicine including a variety of
          genetic testing laboratory partners, precision medicine partners, and
          genetics experts as well as your existing healthcare team to help you
          navigate the healthcare system. Our knowledgeable, compassionate team
          of navigators is ready to help you find a path forward that&apos;s
          tailored to your individual needs.
        </FaqBody>
      </AccordionDetails>
    </MarketingAccordion>

    <MarketingAccordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1 }}>
        <Typography component="span" variant="body_strong_lg_black">
          How are my data and privacy secured?
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <FaqBody>
          Security, privacy, consent, and robust safeguards are at the core of
          everything we do at Genomic Life.
        </FaqBody>
        <Box component="ul" sx={{ ...bulletListSx, gap: 1.5 }}>
          <Box component="li">
            <Typography component="span" variant="body_strong_dark_gray">
              We do not sell data:{" "}
            </Typography>
            <Typography component="span" variant="body_dark_gray">
              Genomic Life does not sell or rent the personal information of our
              members. Learn more in our{" "}
              <Link
                href={GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL}
                rel="noopener noreferrer"
                sx={publicPolicyLinkSx}
                target="_blank"
              >
                privacy policy
              </Link>
              .
            </Typography>
          </Box>
          <Box component="li">
            <Typography component="span" variant="body_strong_dark_gray">
              Security &amp; privacy:{" "}
            </Typography>
            <Typography component="span" variant="body_dark_gray">
              Our HIPAA-compliant platform strictly follows security and privacy
              regulations and has additional safeguards to protect your data,
              including HITRUST, NIST CSF, SOC 2 certifications.
            </Typography>
          </Box>
          <Box component="li">
            <Typography component="span" variant="body_strong_dark_gray">
              3rd party risk management:{" "}
            </Typography>
            <Typography component="span" variant="body_dark_gray">
              We rigorously monitor, review, and manage third-party relationships
              and risks, ensuring that our partners adhere to the highest
              standards of data protection and compliance.
            </Typography>
          </Box>
          <Box component="li">
            <Typography component="span" variant="body_strong_dark_gray">
              State &amp; federal laws:{" "}
            </Typography>
            <Typography component="span" variant="body_dark_gray">
              Our program fully complies with all applicable state and federal
              laws regarding security, privacy, and data protections. If you
              would like to learn more, you can{" "}
              <Link
                href={GENOMIC_LIFE_PUBLIC_POLICIES_URL}
                rel="noopener noreferrer"
                sx={publicPolicyLinkSx}
                target="_blank"
              >
                read our policies
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </MarketingAccordion>

    <MarketingAccordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1 }}>
        <Typography component="span" variant="body_strong_lg_black">
          How do I get the most out of my benefit?
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <FaqBody>
          Everyone&apos;s health journey is unique. To connect you to the right
          services and resources, we will ask you a few questions here in the
          member portal to help us understand your wellness goals, personal
          health, and family history. It takes just a few minutes and matches
          you with suggested services included with your membership.
        </FaqBody>
        <FaqBody>
          Those services may include DNA screening or a meeting with one of our
          knowledgeable, compassionate navigators. And if you ever need 1-on-1
          support navigating the health system, understanding a diagnosis, or
          addressing barriers, simply reach out to our navigation team at{" "}
          <Link href={NAVIGATION_PHONE_TEL} sx={publicPolicyLinkSx}>
            {NAVIGATION_PHONE_DISPLAY}
          </Link>{" "}
          or{" "}
          <Link href={NAVIGATION_MAILTO} sx={publicPolicyLinkSx}>
            {NAVIGATION_EMAIL}
          </Link>
          .
        </FaqBody>
        <FaqBody>
          You set the pace, and we are here to support and guide you.
        </FaqBody>
      </AccordionDetails>
    </MarketingAccordion>

    <MarketingAccordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, py: 1 }}>
        <Typography component="span" variant="body_strong_lg_black">
          What if I have other questions?
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <FaqBody>
          Our dedicated team is here to answer your questions. Simply send us an
          email at{" "}
          <Link href={NAVIGATION_MAILTO} sx={publicPolicyLinkSx}>
            {NAVIGATION_EMAIL}
          </Link>{" "}
          and we will get back to you soon.
        </FaqBody>
      </AccordionDetails>
    </MarketingAccordion>
  </Box>
);

export default InviteeProductFaqAccordions;
